<template>
  <div class="p-6">
    <h2 class="mb-4">Dimensions Manager</h2>

    <div class="flex items-center mb-4 space-x-2">
      <Button label="Create New Dimension" icon="pi pi-plus" @click="openCreate" />
      <Button label="Export to Excel" icon="pi pi-file-excel" @click="exportToExcel" />
      <Button label="Import from Excel" icon="pi pi-upload" @click="openImportDialog" />
      <ProgressSpinner v-if="loading" class="ml-2" />
    </div>
    <div v-if="error" class="mb-4 text-red-600"><strong>Error:</strong> {{ error }}</div>

    <!-- Search Bar -->
    <div class="mb-4">
      <span class="p-input-icon-left">
        <i class="pi pi-search" />
        <InputText
          v-model="searchTerm"
          placeholder="Search dimensions..."
          class="w-full"
          @input="handleSearch"
        />
      </span>
    </div>

    <div v-if="!filteredDimensions.length" class="text-gray-500">
      {{ searchTerm ? 'No dimensions found matching your search.' : 'No dimensions available.' }}
    </div>
    <div v-else class="space-y-4">
      <div
        v-for="dimension in filteredDimensions"
        :key="dimension._id"
        class="p-4 border rounded-lg bg-white shadow-sm"
      >
        <div class="flex justify-between items-start">
          <div class="flex-1">
            <h3>{{ dimension.value }}</h3>
            <p class="text-gray-600">{{ dimension.translation?.en || '' }}</p>
            <p class="text-gray-600">{{ dimension.translation?.de || '' }}</p>
            <p class="text-sm text-gray-500 mt-1">{{ dimension.description?.en || '' }}</p>

            <!-- Question and DevTips counts -->
            <div class="mt-2 flex flex-wrap gap-2">
              <span class="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                {{ dimension.questions?.length || 0 }} Questions
              </span>
              <span
                class="inline-block bg-purple-100 text-purple-800
                 text-xs px-2 py-1 rounded cursor-pointer hover:bg-purple-200 transition-colors"
                @click="viewDevTipsForDimension(dimension.value)"
                :title="`View DevTips for ${dimension.translation?.en || dimension.value}`"
              >
                <i class="pi pi-lightbulb mr-1"></i>
                {{ getDevTipsCountForDimension(dimension.value) }} DevTips
              </span>
            </div>
          </div>

          <div class="flex space-x-2">
            <Button
              label="Edit"
              icon="pi pi-pencil"
              class="p-button-outlined"
              @click="navigateToDetails(dimension._id)"
            />
            <Button
              label="Delete"
              icon="pi pi-trash"
              class="p-button-outlined p-button-danger"
              @click="handleDeleteDimension(dimension)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Create Dialog -->
    <Dialog header="Create New Dimension" v-model:visible="createDialog" modal class="w-3/4">
      <DimensionDialog v-model:visible="createDialog" />
    </Dialog>

    <!-- Import Dialog -->
    <Dialog
      header="Import Dimensions from Excel"
      v-model:visible="importDialog"
      modal
      class="w-3/4"
    >
      <div class="p-4 space-y-4">
        <!-- Step 1: File Upload -->
        <div v-if="importStep === 'upload'" class="space-y-4">
          <div class="mb-4">
            <p class="text-sm text-gray-600 mb-2">
              Upload an Excel file with dimensions. The file should have the following columns:
            </p>
            <ul class="text-sm text-gray-600 list-disc list-inside mb-4">
              <li><strong>value</strong> - Unique identifier for the dimension</li>
              <li><strong>name_en</strong> - English name/translation</li>
              <li><strong>name_de</strong> - German name/translation</li>
              <li><strong>description_en</strong> - English description</li>
              <li><strong>description_de</strong> - German description</li>
            </ul>
            <Button
              label="Download Template"
              icon="pi pi-download"
              class="p-button-text p-button-sm"
              @click="downloadTemplate"
            />
          </div>

          <div class="mb-4">
            <input
              type="file"
              accept=".xlsx,.xls"
              @change="handleFileUpload"
              class="w-full p-2 border border-gray-300 rounded"
            />
          </div>

          <div v-if="importPreview.length > 0" class="mb-4">
            <h4 class="mb-2">Preview ({{ importPreview.length }} dimensions):</h4>
            <div class="max-h-40 overflow-y-auto border rounded p-2">
              <div
                v-for="(item, index) in importPreview.slice(0, 5)"
                :key="index"
                class="text-sm mb-1"
              >
                <strong>{{ item.value }}</strong> - {{ item.translation?.en || 'No English name' }}
              </div>
              <div v-if="importPreview.length > 5" class="text-sm text-gray-500">
                ... and {{ importPreview.length - 5 }} more
              </div>
            </div>
          </div>

          <div v-if="importError" class="text-red-600 text-sm mb-4">
            {{ importError }}
          </div>

          <div class="flex justify-end space-x-2">
            <Button label="Cancel" class="p-button-secondary" @click="closeImportDialog" />
            <Button
              label="Analyze Import"
              icon="pi pi-search"
              @click="analyzeImport"
              :disabled="importPreview.length === 0"
              :loading="analyzing"
            />
          </div>
        </div>

        <!-- Step 2: Conflict Analysis -->
        <div v-if="importStep === 'analysis'" class="space-y-4">
          <div v-if="getImportAnalysis" class="space-y-4">
            <h4 class="text-lg font-semibold">Import Analysis Results</h4>

            <div class="grid grid-cols-2 gap-4">
              <div class="bg-green-50 p-4 rounded-lg border border-green-200">
                <h5 class="font-medium text-green-800">New Dimensions</h5>
                <p class="text-2xl font-bold text-green-600">
                  {{ getImportAnalysis.newDimensions?.length || 0 }}</p>
                <p class="text-sm text-green-700">Will be created</p>
              </div>

              <div class="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <h5 class="font-medium text-yellow-800">Conflicts Found</h5>
                <p class="text-2xl font-bold text-yellow-600">
                  {{ getImportAnalysis.conflicts?.length || 0 }}</p>
                <p class="text-sm text-yellow-700">Need resolution</p>
              </div>
            </div>

            <div v-if="getImportAnalysis.conflicts?.length > 0"
               class="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <h5 class="font-medium text-yellow-800 mb-2">Conflict Details:</h5>
              <div class="max-h-40 overflow-y-auto space-y-2">
                <div
                  v-for="(conflict, index) in getImportAnalysis.conflicts.slice(0, 3)"
                  :key="index"
                  class="text-sm bg-white p-2 rounded border"
                >
                  <strong>{{ conflict.newDimension.value }}</strong>
                  <div class="text-xs text-gray-600">
                    <div>Existing: {{ conflict.existing.translation?.en
                     || conflict.existing.value }}</div>
                    <div>New: {{ conflict.newDimension.translation?.en
                     || conflict.newDimension.value }}</div>
                  </div>
                </div>
                <div v-if="getImportAnalysis.conflicts.length > 3" class="text-xs text-gray-500">
                  ... and {{ getImportAnalysis.conflicts.length - 3 }} more conflicts
                </div>
              </div>
            </div>

            <div v-if="getImportAnalysis.duplicates?.length > 0"
               class="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h5 class="font-medium text-blue-800">Duplicates Found</h5>
              <p class="text-sm text-blue-700">
                {{
                   getImportAnalysis.duplicates.length
                }} dimensions already exist with identical data
              </p>
            </div>
          </div>

          <div class="flex justify-end space-x-2">
            <Button label="Back" class="p-button-secondary" @click="importStep = 'upload'" />
            <Button
              label="Skip Conflicts"
              icon="pi pi-step-forward"
              class="p-button-outlined"
              @click="executeImport('skip')"
              :loading="importing"
            />
            <Button
              label="Override Conflicts"
              icon="pi pi-arrow-right-arrow-left"
              class="p-button-warning"
              @click="executeImport('override')"
              :loading="importing"
            />
          </div>
        </div>

        <!-- Step 3: Import Progress -->
        <div v-if="importStep === 'progress'" class="space-y-4">
          <div v-if="importSummary" class="space-y-4">
            <h4 class="text-lg font-semibold">Import Completed Successfully!</h4>

            <div class="grid grid-cols-3 gap-4">
              <div class="bg-green-50 p-4 rounded-lg border border-green-200 text-center">
                <p class="text-2xl font-bold text-green-600">{{ importSummary.new }}</p>
                <p class="text-sm text-green-700">New Dimensions</p>
              </div>

              <div class="bg-yellow-50 p-4 rounded-lg border border-yellow-200 text-center">
                <p class="text-2xl font-bold text-yellow-600">{{ importSummary.willUpdate }}</p>
                <p class="text-sm text-yellow-700">Updated Dimensions</p>
              </div>

              <div class="bg-blue-50 p-4 rounded-lg border border-blue-200 text-center">
                <p class="text-2xl font-bold text-blue-600">{{ importSummary.duplicates }}</p>
                <p class="text-sm text-blue-700">Skipped (Duplicates)</p>
              </div>
            </div>

            <div class="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h5 class="font-medium text-gray-800 mb-2">Import Summary:</h5>
              <p class="text-sm text-gray-700">
                Total processed: <strong>{{ importSummary.total }}</strong> dimensions
              </p>
            </div>
          </div>

          <div class="flex justify-end space-x-2">
            <Button
              v-if="canRollbackImport"
              label="Rollback Import"
              icon="pi pi-undo"
              class="p-button-outlined p-button-warning"
              @click="rollbackImport"
              :loading="rollingBack"
            />
            <Button
              label="Close"
              class="p-button-primary"
              @click="closeImportDialog"
            />
          </div>
        </div>
      </div>
    </Dialog>
  </div>

  <!-- Confirm Dialog for Conflict Resolution -->
  <ConfirmDialog />
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';
import InputText from 'primevue/inputtext';
import ConfirmDialog from 'primevue/confirmdialog';
import DimensionDialog from '../components/DimensionDialog.vue';
import * as XLSX from 'xlsx';

export default {
  name: 'DimensionsManager',
  components: {
    Dialog,
    Button,
    ProgressSpinner,
    InputText,
    ConfirmDialog,
    DimensionDialog,
  },
  data() {
    return {
      createDialog: false,
      importDialog: false,
      searchTerm: '',
      importPreview: [],
      importError: null,
      importing: false,
      importStep: 'upload', // 'upload', 'analysis', 'progress'
      analyzing: false,
      rollingBack: false,
    };
  },
  computed: {
    ...mapState('dimensions', {
      dimensions: (state) => state.dimensions,
      loading: (state) => state.loading,
      error: (state) => state.error,
    }),
    ...mapState('devTips', {
      devTips: (state) => state.tips,
      devTipsLoading: (state) => state.loading,
    }),
    ...mapGetters('dimensions', [
      'searchDimensions',
      'hasImportConflicts',
      'importSummary',
      'importStatus',
      'canRollbackImport',
      'getImportAnalysis',
    ]),
    ...mapGetters('devTips', ['tipsByDimension']),
    filteredDimensions() {
      if (!this.searchTerm) {
        return this.dimensions;
      }
      return this.searchDimensions(this.searchTerm);
    },
  },
  mounted() {
    this.fetchDimensions();
    this.fetchDevTips();
  },
  methods: {
    ...mapActions('dimensions', [
      'fetchDimensions',
      'deleteDimension',
      'analyzeImportConflicts',
      'executeImportWithChoice',
      'rollbackLastImport',
      'clearImportState',
    ]),
    ...mapActions('devTips', ['loadTips', 'loadAllTips']),

    openCreate() {
      this.createDialog = true;
    },

    openImportDialog() {
      this.importDialog = true;
      this.importStep = 'upload';
      this.importPreview = [];
      this.importError = null;
      this.analyzing = false;
      this.rollingBack = false;
      // Clear any previous import state
      this.clearImportState();
    },

    navigateToDetails(dimensionId) {
      this.$router.push(`/contentManagement/dimensions/${dimensionId}`);
    },

    async handleDeleteDimension(dimension) {
      try {
        await this.deleteDimension(dimension._id);
      } catch (e) {
        // Error handling is done in the store
      }
    },

    handleSearch() {
      // Search is handled by the computed property
    },

    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.importError = null;
      this.importPreview = [];

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });

          console.log('Available sheets:', workbook.SheetNames);

          // Read all worksheets
          const dimensionsData = this.readWorksheet(workbook, 'Dimensions');
          const questionsData = this.readWorksheet(workbook, 'Questions');
          const resultDescriptionsData = this.readWorksheet(workbook, 'Result Descriptions');

          console.log('Excel data loaded:', {
            dimensions: dimensionsData.length,
            questions: questionsData.length,
            resultDescriptions: resultDescriptionsData.length,
          });

          if (dimensionsData.length === 0) {
            this.importError = 'No "Dimensions" sheet found in the Excel file. Please ensure your file has a "Dimensions" worksheet.';
            return;
          }

          if (resultDescriptionsData.length > 0) {
            console.log('Sample result descriptions data:', resultDescriptionsData.slice(0, 3));
          }

          // Transform the dimensions data to match exact model structure
          this.importPreview = dimensionsData
            .map((row) => ({
              value: row.value || row.Value || '',
              translation: {
                en: row.translation_en || row.translationEn || '',
                de: row.translation_de || row.translationDe || '',
              },
              description: {
                en: row.description_en || row.descriptionEn || '',
                de: row.description_de || row.descriptionDe || '',
              },
              resultDescriptions: [],
              questions: [],
            }))
            .filter((item) => item.value && item.translation.en &&
             item.translation.de); // Filter out incomplete rows

          console.log('Processed importPreview:', this.importPreview);

          if (this.importPreview.length === 0) {
            this.importError = 'No valid dimension data found. Please check your Excel file format.';
            return;
          }

          // Map questions to dimensions with exact model structure
          if (questionsData.length > 0) {
            questionsData.forEach((questionRow) => {
              const dimensionValue = questionRow.dimension_value;
              const dimension = this.importPreview.find((d) => d.value === dimensionValue);

              if (dimension) {
                const question = {
                  class: 'question_element',
                  type: this.validateQuestionType(questionRow.type) || 'rangeSlider',
                  viewTimeLogs: [],
                  properties: {
                    dimensionName: questionRow.dimensionName || '',
                    dimension: questionRow.dimension || '',
                    content: {
                      text_questionProgress: {
                        en: questionRow.content_text_questionProgress_en || '',
                        de: questionRow.content_text_questionProgress_de || '',
                      },
                      text_question: {
                        en: questionRow.content_text_question_en || '',
                        de: questionRow.content_text_question_de || '',
                      },
                    },
                    options: {
                      forceInput: questionRow.options_forceInput || false,
                    },
                    data: {
                      mindsetType: questionRow.data_mindsetType || '',
                      positive: questionRow.data_positive || false,
                      value: questionRow.data_value || '',
                    },
                  },
                };
                dimension.questions.push(question);
              }
            });
          }

          // Map result descriptions to dimensions with exact model structure
          if (resultDescriptionsData.length > 0) {
            console.log('Processing result descriptions:', resultDescriptionsData.length, 'rows');
            resultDescriptionsData.forEach((descRow, index) => {
              const dimensionValue = descRow.dimension_value;
              const dimension = this.importPreview.find((d) => d.value === dimensionValue);

              if (dimension) {
                const resultDescription = {
                  headline: {
                    en: descRow.headline_en || '-',
                    de: descRow.headline_de || '-',
                  },
                  content: {
                    en: descRow.content_en || '-',
                    de: descRow.content_de || '-',
                  },
                  tip: {
                    en: descRow.tip_en || '-',
                    de: descRow.tip_de || '-',
                  },
                  status: descRow.status || 'positive',
                };

                console.log(`Result description ${index + 1} for dimension ${dimensionValue}:`, {
                  status: resultDescription.status,
                  headline_en: resultDescription.headline.en,
                  content_en: resultDescription.content.en,
                  tip_en: resultDescription.tip.en,
                });

                // Always add the result description (no validation filtering)
                dimension.resultDescriptions.push(resultDescription);
                console.log(`✅ Added result description for ${dimensionValue}`);
              } else {
                console.warn(`⚠️ Dimension not found for result description: ${dimensionValue}`);
              }
            });
          }

          if (this.importPreview.length === 0) {
            this.importError =
              'No valid dimensions found in the file. Please check the column names.';
          }

          console.log('Final importPreview with questions and descriptions:', this.importPreview);
        } catch (error) {
          console.error('Error processing Excel file:', error);
          this.importError = `Failed to process Excel file: ${error.message}`;
          this.importPreview = [];
        }
      };

      reader.onerror = () => {
        this.importError = 'Failed to read the uploaded file.';
        this.importPreview = [];
      };

      reader.readAsArrayBuffer(file);
    },

    readWorksheet(workbook, sheetName) {
      if (!workbook.SheetNames.includes(sheetName)) {
        return [];
      }
      const worksheet = workbook.Sheets[sheetName];
      return XLSX.utils.sheet_to_json(worksheet);
    },

    async importDimensions() {
      if (this.importPreview.length === 0) return;

      this.importing = true;
      this.importError = null;

      try {
        // Use the new store action to analyze conflicts first
        const analysis = await this.$store.dispatch('dimensions/analyzeImportConflicts', {
          excelData: this.importPreview,
          existingDimensions: this.dimensions,
        });

        // Check if there are conflicts
        if (analysis.conflicts.length > 0) {
          // Show conflict resolution dialog
          this.$confirm.require({
            message: `Found ${analysis.conflicts.length} conflicts. How would you like to handle them?`,
            header: 'Conflict Resolution',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Override All',
            rejectLabel: 'Skip All',
            accept: () => this.executeImportWithChoice('override'),
            reject: () => this.executeImportWithChoice('skip'),
          });
        } else {
          // No conflicts, proceed with import
          await this.executeImportWithChoice('skip');
        }
      } catch (error) {
        this.importError = `Import failed: ${error.message}`;
        this.importing = false;
      }
    },

    async executeImportWithChoice(userChoice) {
      try {
        const result = await this.$store.dispatch('dimensions/executeImportWithChoice', {
          userChoice,
        });

        // Show success message
        this.$toast.add({
          severity: 'success',
          summary: 'Import Successful',
          detail: `Successfully imported ${result.imported.length} dimensions${
            result.overridden.length > 0 ? `, ${result.overridden.length} updated` : ''
          }${result.skipped.length > 0 ? `, ${result.skipped.length} skipped` : ''}`,
          life: 5000,
        });

        this.closeImportDialog();
        this.fetchDimensions();
      } catch (error) {
        this.importError = `Import failed: ${error.message}`;
      } finally {
        this.importing = false;
      }
    },

    exportToExcel() {
      try {
        // Create multiple worksheets for comprehensive export
        const workbook = XLSX.utils.book_new();

        // Worksheet 1: Basic dimension information
        const basicData = this.filteredDimensions.map((dimension) => ({
          value: dimension.value,
          translation_en: dimension.translation?.en || '',
          translation_de: dimension.translation?.de || '',
          description_en: dimension.description?.en || '',
          description_de: dimension.description?.de || '',
          questions_count: dimension.questions?.length || 0,
          result_descriptions_count: dimension.resultDescriptions?.length || 0,
        }));

        const basicWorksheet = XLSX.utils.json_to_sheet(basicData);
        basicWorksheet['!cols'] = [
          { wch: 20 }, // value
          { wch: 30 }, // translation_en
          { wch: 30 }, // translation_de
          { wch: 40 }, // description_en
          { wch: 40 }, // description_de
          { wch: 15 }, // questions_count
          { wch: 20 }, // result_descriptions_count
        ];
        XLSX.utils.book_append_sheet(workbook, basicWorksheet, 'Dimensions');

        // Worksheet 2: Questions (if any dimensions have questions)
        const allQuestions = [];
        this.filteredDimensions.forEach((dimension) => {
          if (dimension.questions && dimension.questions.length > 0) {
            dimension.questions.forEach((question, questionIndex) => {
              allQuestions.push({
                dimension_value: dimension.value,
                question_index: questionIndex,
                class: question.class || 'question_element',
                type: question.type || 'rangeSlider',
                dimensionName: question.properties?.dimensionName || '',
                dimension: question.properties?.dimension || '',
                content_text_questionProgress_en:
                  question.properties?.content?.text_questionProgress?.en || '',
                content_text_questionProgress_de:
                  question.properties?.content?.text_questionProgress?.de || '',
                content_text_question_en: question.properties?.content?.text_question?.en || '',
                content_text_question_de: question.properties?.content?.text_question?.de || '',
                options_forceInput: question.properties?.options?.forceInput || false,
                data_mindsetType: question.properties?.data?.mindsetType || '',
                data_positive: question.properties?.data?.positive || false,
                data_value: question.properties?.data?.value || '',
              });
            });
          }
        });

        if (allQuestions.length > 0) {
          const questionsWorksheet = XLSX.utils.json_to_sheet(allQuestions);
          questionsWorksheet['!cols'] = [
            { wch: 20 }, // dimension_value
            { wch: 10 }, // question_index
            { wch: 20 }, // class
            { wch: 20 }, // type
            { wch: 20 }, // dimensionName
            { wch: 20 }, // dimension
            { wch: 40 }, // content_text_questionProgress_en
            { wch: 40 }, // content_text_questionProgress_de
            { wch: 40 }, // content_text_question_en
            { wch: 40 }, // content_text_question_de
            { wch: 15 }, // options_forceInput
            { wch: 20 }, // data_mindsetType
            { wch: 10 }, // data_positive
            { wch: 10 }, // data_value
          ];
          XLSX.utils.book_append_sheet(workbook, questionsWorksheet, 'Questions');
        }

        // Worksheet 3: Result Descriptions (if any dimensions have result descriptions)
        const allResultDescriptions = [];
        this.filteredDimensions.forEach((dimension) => {
          if (dimension.resultDescriptions && dimension.resultDescriptions.length > 0) {
            dimension.resultDescriptions.forEach((resultDesc, descIndex) => {
              allResultDescriptions.push({
                dimension_value: dimension.value,
                description_index: descIndex,
                headline_en: resultDesc.headline?.en || '',
                headline_de: resultDesc.headline?.de || '',
                content_en: resultDesc.content?.en || '',
                content_de: resultDesc.content?.de || '',
                tip_en: resultDesc.tip?.en || '',
                tip_de: resultDesc.tip?.de || '',
                status: resultDesc.status || 'positive',
              });
            });
          }
        });

        if (allResultDescriptions.length > 0) {
          const resultDescWorksheet = XLSX.utils.json_to_sheet(allResultDescriptions);
          resultDescWorksheet['!cols'] = [
            { wch: 20 }, // dimension_value
            { wch: 10 }, // description_index
            { wch: 30 }, // headline_en
            { wch: 30 }, // headline_de
            { wch: 40 }, // content_en
            { wch: 40 }, // content_de
            { wch: 40 }, // tip_en
            { wch: 40 }, // tip_de
            { wch: 12 }, // status
          ];
          XLSX.utils.book_append_sheet(workbook, resultDescWorksheet, 'Result Descriptions');
        }

        // Generate filename with current date
        const date = new Date().toISOString().split('T')[0];
        const filename = `dimensions_complete_export_${date}.xlsx`;

        // Save file
        XLSX.writeFile(workbook, filename);

        this.$toast.add({
          severity: 'success',
          summary: 'Export Successful',
          detail: `Exported ${basicData.length} dimensions with ${allQuestions.length} questions and ${allResultDescriptions.length} result descriptions to ${filename}`,
          life: 3000,
        });
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Export Failed',
          detail: `Failed to export dimensions: ${error.message}`,
          life: 5000,
        });
      }
    },

    closeImportDialog() {
      this.importDialog = false;
      this.importPreview = [];
      this.importError = null;
      this.importStep = 'upload';
      this.analyzing = false;
      this.rollingBack = false;
      // Reset file input
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';
      // Clear the store's import state
      this.clearImportState();
    },

    async analyzeImport() {
      console.log('analyzeImport called, importPreview:', this.importPreview);

      if (!this.importPreview || this.importPreview.length === 0) {
        this.importError = 'No data to analyze. Please upload an Excel file first.';
        return;
      }

      this.analyzing = true;
      this.importError = null;

      try {
        console.log('Calling analyzeImportConflicts with:', this.importPreview);
        await this.analyzeImportConflicts({
          excelData: this.importPreview,
          existingDimensions: this.dimensions,
        });
        this.importStep = 'analysis';
      } catch (error) {
        console.error('Error in analyzeImport:', error);
        this.importError = error.message;
      } finally {
        this.analyzing = false;
      }
    },

    async executeImport(choice) {
      this.importing = true;
      this.importError = null;

      try {
        await this.executeImportWithChoice(choice);
        this.importStep = 'progress';
      } catch (error) {
        this.importError = error.message;
      } finally {
        this.importing = false;
      }
    },

    async rollbackImport() {
      this.rollingBack = true;

      try {
        await this.rollbackLastImport();
        this.importStep = 'upload';
        this.importPreview = [];
        this.importError = null;
      } catch (error) {
        this.importError = error.message;
      } finally {
        this.rollingBack = false;
      }
    },

    downloadTemplate() {
      try {
        // Create sample data for template
        const templateData = [
          {
            value: 'leadership',
            translation_en: 'Leadership',
            translation_de: 'Führung',
            description_en: 'Leadership and management capabilities',
            description_de: 'Führungs- und Managementfähigkeiten',
          },
          {
            value: 'communication',
            translation_en: 'Communication',
            translation_de: 'Kommunikation',
            description_en: 'Communication and interpersonal skills',
            description_de: 'Kommunikations- und zwischenmenschliche Fähigkeiten',
          },
          {
            value: 'problem_solving',
            translation_en: 'Problem Solving',
            translation_de: 'Problemlösung',
            description_en: 'Analytical and problem-solving abilities',
            description_de: 'Analytische und Problemlösungsfähigkeiten',
          },
        ];

        // Create workbook and worksheet
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(templateData);

        // Set column widths
        const columnWidths = [
          { wch: 20 }, // value
          { wch: 30 }, // translation_en
          { wch: 30 }, // translation_de
          { wch: 40 }, // description_en
          { wch: 40 }, // description_de
        ];
        worksheet['!cols'] = columnWidths;

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Template');

        // Save file
        XLSX.writeFile(workbook, 'dimensions_import_template.xlsx');

        this.$toast.add({
          severity: 'info',
          summary: 'Template Downloaded',
          detail: 'Template file downloaded successfully',
          life: 3000,
        });
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Download Failed',
          detail: `Failed to download template: ${error.message}`,
          life: 5000,
        });
      }
    },

    validateQuestionType(type) {
      // Valid question types from the model enum
      const validTypes = ['rangeSlider', 'integrativeComplexity', 'chooseFromList', 'textField'];
      return validTypes.includes(type) ? type : null;
    },

    // DevTips methods
    async fetchDevTips() {
      try {
        // Load all DevTips using pagination to bypass backend limit of 100
        await this.loadTips(); // This will automatically use pagination to get all tips
      } catch (error) {
        console.error('Failed to fetch DevTips:', error);
      }
    },

    async refreshDevTips() {
      try {
        // Force refresh all DevTips using pagination
        await this.loadAllTips();
        console.log('DevTips refreshed, total count:', this.tips.length);
      } catch (error) {
        console.error('Failed to refresh DevTips:', error);
      }
    },

    getDevTipsCountForDimension(dimensionValue) {
      const tips = this.tipsByDimension(dimensionValue);
      return tips.length;
    },

    viewDevTipsForDimension(dimensionValue) {
      // Navigate to DevTips Manager with dimension filter
      this.$router.push({
        path: '/contentManagement/dev-tips',
        query: { dimension: dimensionValue },
      });
    },
  },
};
</script>

<style scoped></style>
